<template>
  <div class="career-tracks">
    <HeroSection
      :title="$c.text('pages.career-tracks.heroTitle')"
      :subtitle="$c.text('pages.career-tracks.heroSubtitle')"
      :button-title="$store.state.layout === 'desktop' || $store.state.tablet ? 'Start Your Career' : 'Start Course'"
      button-link="/signup/"
      :image-url="heroImage"
      is-body1
      class-name="career-tracks-page"
      mdcolumns="md-5"
    />
    <div class="container tracking-section career-track-section" data-event="desired_job_title.scroll">
      <div class="site-section">
        <h2 class="h2">
          {{ $c.text('pages.career-tracks.desiredJob.title') }}
        </h2>
        <div class="sliders">
          <div v-for="(track, idx) in tracks" :key="idx" class="row">
            <SliderCourses
              :track="track"
              certificate
              dark
            />
          </div>
        </div>
      </div>
    </div>
    <div class="footer-section container">
      <div class="footer col-6">
        <p class="text subtitle">
          {{ $c.text('pages.career-tracks.footerText') }}
        </p>
        <nuxt-link to="/signup/" class="btn-primary btn-lg" data-event="start_your_career_2.click">
          {{ $c.text($config.platform === 'ds' ? 'button.start-your-career' : 'button.get-started') }}
        </nuxt-link>
      </div>
    </div>
    <Lazy :when-visible="{ rootMargin: '200px' }">
      <div class="dream-job tracking-section" data-event="get_your_dream_job.scroll">
        <div class="container">
          <h3 class="h2" v-html="$c.text('pages.career-tracks.getYourDreamJob.title')" />
          <div class="content">
            <div class="track-line">
              <div class="track-line-filler" />
            </div>
            <div v-for="(line, i) in trackLines" :key="i" class="row">
              <div v-if="line.number % 2 !== 0" class="col col-5">
                <Animation class="anim" :options="{ data: line.animation }" />
              </div>
              <div :class="line.number % 2 === 0 ? 'text col col-4 md-5' : 'text col col-5 offset-2'">
                <p class="h3">
                  {{ line.heading }}
                </p>
                <p class="body-1">
                  {{ line.description }}
                </p>
                <p class="body-1">
                  {{ line.caption }}
                </p>
              </div>
              <div v-if="line.number % 2 === 0" class="col col-5 offset-3 md-offset-2">
                <Animation class="anim" :options="{ data: line.animation }" />
              </div>
              <div class="track-dot" :class="`dot-${i + 1}`">
                <span>
                  {{ line.number }}</span>
              </div>
            </div>
            <CertificateBadge class="track-badge" />
          </div>
          <p class="track-badge-text h3">
            Receive Certificate
          </p>
          <img
            loading="lazy"
            class="track-certificate"
            :src="certificateImage"
            alt="Career track certificate"
          >
        </div>
      </div>
    </Lazy>
    <PreFooter
      :title="$c.text('pages.career-tracks.preFooter.title')"
      :description="$c.text('pages.career-tracks.preFooter.subtitle')"
      :button-title="$c.text($config.platform === 'ds' ? 'button.start-your-career' : 'button.get-started')"
      button-link="/signup/"
      class="tracking-section"
      data-event="own_your_career.scroll"
      columns="col-10"
      s-columns="col-8"
    />
  </div>
</template>

<script>
import Lazy from 'vue-lazy-hydration';
import global from '@itbpbg/lms-components/src/mixins/global';
import HeroSection from '@itbpbg/lms-components/src/components/HeroSection';
import CertificateBadge from '@itbpbg/lms-components/src/assets/svg/certificate-badge.svg?vue-component';
import SliderCourses from '@itbpbg/lms-components/src/Career-tracks/SliderCourses';
import PreFooter from '../../components/PreFooter';
import Animation from '../../components/Animation';

export default {
  components: {
    Lazy,
    HeroSection,
    Animation,
    CertificateBadge,
    PreFooter,
    SliderCourses,
  },
  mixins: [global],
  async asyncData({ $axios, route, $config }) {
    const [tracks, page] = await Promise.all([
      $axios.$get('/career-tracks').catch(() => false) || [],
      $axios.$get(`/pages/${route.fullPath.replace(/\//g, '')}`).catch(() => false) || {},
    ]);
    page.fullPath = `${$config.siteUrl}${route.fullPath}`;

    return {
      tracks,
      page,
    };
  },
  head() {
    const tracksList = [];

    this.tracks.forEach((item, i) => {
      tracksList.push({
        '@type': 'ListItem',
        position: i + 1,
        url: `${this.$config.siteUrl}/career-tracks/${item.slug}/`,
      });
    });

    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
      {
        key: 'CoursesList',
        values: {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: tracksList || [],
        },
      },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps(this.page),
      script: [linkedData],
    };
  },
  computed: {
    heroImage() {
      if (this.$config.platform === 'fa') {
        if (this.$store.state.layout === 'desktop' || this.$store.state.tablet) {
          return 'fa-career-track-bg.jpg';
        }
        return 'fa-career-track-bg-mobile.jpg';
      }
      return 'career-track-bg.jpg';
    },
    certificateImage() {
      if (this.$config.platform === 'fa') {
        return require('../../assets/images/fa/fa-career-track-certificate.png');
      }
      return require('../../assets/images/career-track-certificate.png');
    },
    animationEnroll() {
      if (this.$config.platform === 'fa') {
        return 'fa/fa-career-track-enroll';
      }
      return 'career-track-enroll';
    },
    animationElective() {
      if (this.$config.platform === 'fa') {
        return 'fa/fa-career-track-choose-electives';
      }
      return 'career-track-choose-electives';
    },
    animationClasses() {
      if (this.$config.platform === 'fa') {
        return 'fa/fa-career-track-take-classes';
      }
      return 'career-track-take-classes';
    },
    animationExams() {
      if (this.$config.platform === 'fa') {
        return 'fa/fa-career-track-take-exams';
      }
      return 'career-track-take-exams';
    },
    animationFinalExams() {
      if (this.$config.platform === 'fa') {
        return 'fa/fa-career-track-final-exam';
      }
      return 'career-track-final-exam';
    },
    trackLines() {
      return [
        {
          animation: this.animationEnroll,
          heading: this.$c.text('pages.career-tracks.getYourDreamJob.anim1.heading'),
          description: this.$c.text('pages.career-tracks.getYourDreamJob.anim1.description'),
          caption: this.$c.text('pages.career-tracks.getYourDreamJob.anim1.caption'),
          number: 1,
        },
        {
          animation: this.animationElective,
          heading: this.$c.text('pages.career-tracks.getYourDreamJob.anim2.heading'),
          description: this.$c.text('pages.career-tracks.getYourDreamJob.anim2.description'),
          caption: this.$c.text('pages.career-tracks.getYourDreamJob.anim2.caption'),
          number: 2,
        },
        {
          animation: this.animationClasses,
          heading: this.$c.text('pages.career-tracks.getYourDreamJob.anim3.heading'),
          description: this.$c.text('pages.career-tracks.getYourDreamJob.anim3.description'),
          caption: this.$c.text('pages.career-tracks.getYourDreamJob.anim3.caption'),
          number: 3,
        },
        {
          animation: this.animationExams,
          heading: this.$c.text('pages.career-tracks.getYourDreamJob.anim4.heading'),
          description: this.$c.text('pages.career-tracks.getYourDreamJob.anim4.description'),
          caption: this.$c.text('pages.career-tracks.getYourDreamJob.anim4.caption'),
          number: 4,
        },
        {
          animation: this.animationFinalExams,
          heading: this.$c.text('pages.career-tracks.getYourDreamJob.anim5.heading'),
          description: this.$c.text('pages.career-tracks.getYourDreamJob.anim5.description'),
          caption: this.$c.text('pages.career-tracks.getYourDreamJob.anim5.caption'),
          number: 5,
        },
      ];
    },
  },
  mounted() {
    this.scrollAnimation();
    window.addEventListener('scroll', this.scrollAnimation);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.scrollAnimation));
  },
  methods: {
    scrollAnimation() {
      if (!this.$el.querySelector('.dream-job')) {
        return;
      }
      const dots = this.$el.querySelectorAll('.track-dot');
      const line = this.$el.querySelector('.track-line');
      const lineFiller = this.$el.querySelector('.track-line .track-line-filler');
      const certificate = this.$el.querySelector('.track-certificate');
      const badge = this.$el.querySelector('.track-badge');
      const badgeBound = badge.getBoundingClientRect();
      let lineHeight = (line.getBoundingClientRect().top - window.innerHeight / 2) * -1;
      const scrolledToBottom = window.innerHeight + (document.documentElement.scrollTop || document.body.scrollTop) >= document.body.scrollHeight;

      if (lineHeight < 0) {
        lineHeight = 0;
      } else if (scrolledToBottom) {
        lineHeight = line.getBoundingClientRect().height;
      }

      dots.forEach((dot, i) => {
        setTimeout(() => {
          if ((lineHeight > dot.offsetParent.offsetTop && i === 0) || (lineHeight >= dot.offsetParent.offsetTop && i > 0)) {
            dot.classList.add('filled');
          } else {
            dot.classList.remove('filled');
          }
        }, 300);
      });

      setTimeout(() => {
        lineFiller.style.height = `${lineHeight}px`;
      }, 200);

      if (badgeBound.top - window.innerHeight / 2 < 0 || scrolledToBottom) {
        badge.classList.add('filled');
        certificate.classList.add('filled');
      } else {
        badge.classList.remove('filled');
        certificate.classList.remove('filled');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.career-tracks {
  .hero-section-wrapper {
    ::v-deep .image-container {
      &:after {
        display: none;
      }

      .row {
        .heading {
          .subtitle {
            margin-bottom: var(--spacing-48);
            font-size: 18px;
            line-height: 150%;
          }
        }
      }
    }
  }

  .site-section {
    padding: var(--spacing-100) 0 0;

    .h2 {
      text-align: center;
      margin-bottom: var(--spacing-36);

      @include bp($bp-phone) {
        text-align: left;

        br {
          display: none;
        }
      }
    }

    .sliders {
      .row {
        margin: 0;
      }
    }

    .content {
      margin-top: var(--spacing-36);

      .col {
        &:nth-child(2n + 2) {
          margin-top: 70px;
        }

        &:nth-child(odd) {
          padding-left: 95px;
          padding-right: 35px;
        }

        &:nth-child(even) {
          padding-left: 35px;
          padding-right: 95px;
        }
      }
    }
  }

  .footer-section {
    padding-top: var(--spacing-60);
    padding-bottom: var(--spacing-120);
  }

  .footer {
    margin: 0 auto;
    text-align: center;

    .text {
      margin: 0 0 var(--spacing-20);
    }

    a {
      max-width: 300px;
      font-weight: 600;
      width: 100%;
    }
  }

  .dream-job {
    background-color: var(--main-dark);
    position: relative;
    padding: var(--spacing-100) 0 var(--spacing-120);
    color: var(--main-light);

    .container {
      position: relative;
      z-index: 1;

      .h2 {
        text-align: center;
        margin-bottom: 0;

        @include bp($bp-phone) {
          br {
            display: none;
          }
        }
      }
    }

    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(242.72deg, rgba(0, 164, 178, 0.14) 15.48%, rgba(0, 164, 178, 0.05) 49.12%, rgba(0, 12, 31, 0) 73.63%);
      transform: matrix(-1, 0, 0, 1, 0, 0);
    }

    .content {
      margin-top: var(--spacing-80);
      position: relative;
    }

    .row {
      position: relative;
      align-items: center;

      .body-1 + .body-1 {
        margin-top: var(--spacing-36);
        color: var(--main-light-shade-4);
      }

      + .row {
        margin-top: var(--spacing-120);
      }

      .track-dot {
        position: absolute;
        left: 50%;
        margin-left: -18px;
        top: 10px;

        span {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 600;
          line-height: 110%;
          position: relative;
          z-index: 1;
          width: var(--spacing-36);
          height: var(--spacing-36);
          border-radius: 50%;
          background-color: var(--main-dark-shade-2);
          color: var(--main-dark-shade-3);
          transition: all var(--duration) ease-in;
        }

        &:before {
          content: '';
          border-width: 12px;
          border-style: solid;
          border-radius: 50%;
          display: block;
          position: absolute;
          top: -12px;
          left: -12px;
          width: 36px;
          height: 36px;
          z-index: 0;
        }

        &.dot-1:before {
          border-color: #071B2F;
        }

        &.dot-2:before {
          border-color: #00192B;
        }

        &.dot-3:before {
          border-color: #001729;
        }

        &.dot-4:before {
          border-color: #001326;
        }

        &.dot-5:before {
          border-color: #001023;
        }

        &.filled span {
          background-color: var(--accent);
          color: var(--main-light);
        }
      }
    }

    .track-line {
      background: var(--main-dark-shade-2);
      position: absolute;
      height: 100%;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 2px;
      z-index: 0;

      .track-line-filler {
        background-color: var(--accent);
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 0;
        max-height: 100%;
        transition: all 500ms ease-out;
      }

      + * {
        z-index: 1;
      }
    }

    .track-badge {
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translate(-50%, 0);
      margin-top: 10px;

      path {
        transition: all var(--duration) ease-in;
        transition-delay: 500ms;
      }

      &.filled {
        path {
          transition-delay: 0;

          &[fill] {
            fill: var(--accent);
          }

          &[stroke] {
            stroke: var(--accent);
          }
        }
      }
    }

    .track-badge-text {
      display: none;
    }

    .track-certificate {
      display: block;
      margin: 72px auto 0 auto;
      opacity: 0.5;
      transition: all 500ms ease-in;
      transition-delay: 500ms;

      &.filled {
        opacity: 1;
      }
    }
  }

  ::v-deep .pre-footer {
    .container {
      .row {
        .col {
          .subtitle {
            width: 100%;
            max-width: 770px;
          }
        }

        .row-btn a {
          margin: 0 auto;
          width: 320px;
          height: 56px;
          font-weight: 600;
        }
      }
    }

    .bg {
      margin-top: var(--spacing-100);
    }
  }
}

@include bp($bp-mobile) {
  .career-tracks {
    .site-section {
      padding-top: var(--spacing-80);

      .content {
        .col {
          margin-top: 24px;

          &:nth-child(2n + 2) {
            margin-top: 24px;
          }

          &:nth-child(even),
          &:nth-child(odd) {
            padding-left: 12px;
            padding-right: 12px;
          }
        }
      }
    }

    .career-track-section {
      background: var(--main-light-shade-2);
      padding-bottom: 62px;
    }

    .dream-job {
      padding-bottom: var(--spacing-80);

      &:before {
        content: none;
      }

      .content {
        padding-bottom: 35px;

        .anim {
          max-width: 500px;
        }
      }

      .track-line {
        left: 15px;
      }

      .row {
        padding-left: 60px;

        .text {
          order: -1;
        }

        .track-dot {
          left: 12px;
          margin-left: 0;

          &.dot-1:before,
          &.dot-2:before,
          &.dot-3:before,
          &.dot-4:before,
          &.dot-5:before,
          &.dot-6:before {
            border-color: #020C20;
          }
        }

        + .row {
          margin-top: 35px;
        }

        .col-4 {
          padding-top: var(--spacing-8);
        }
      }

      .track-badge {
        left: 15px;
      }

      .track-badge-text {
        display: block;
        margin-top: 10px;
        padding-left: 60px;
      }

      .track-certificate {
        width: calc(100% - 60px);
        max-width: 500px;
        margin-left: var(--spacing-60);
      }
    }
  }
}

@include bp($bp-phone) {
  .career-tracks {
    ::v-deep .hero-section-wrapper {
      &:after {
        content: '';
        position: absolute;
        display: block;
        top: 302px;
        left: 0;
        width: 100%;
        height: 150px;
        background: linear-gradient(0deg, #000C1F 0%, rgba(0, 12, 31, 0) 100%);
      }

      .image-container {
        padding-top: var(--spacing-48);

        .heading {
          .h2 {
            font-size: 32px;
          }

          .subtitle {
            font-size: 16px !important;
            color: var(--main-light-shade-3);
          }

          .btn-primary {
            font-weight: 600;
          }
        }
      }
    }

    .site-section {
      .h2 {
        margin-bottom: var(--spacing-48);
      }

      .sliders {
        .row {
          &:not(:first-of-type) {
            margin-top: var(--spacing-100);
          }

          ::v-deep .track-slider {
            .slider-container {
              .dots {
                padding-top: var(--spacing-20);
                padding-bottom: var(--spacing-40);
              }
            }

            .view-details {
              width: 100%;
              height: 52px;
              font-weight: 600;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }
        }
      }
    }

    .career-track-section {
      padding-bottom: var(--spacing-80);
    }

    .footer-section {
      padding: var(--spacing-80) var(--spacing-24);

      .footer {
        .btn-primary {
          width: 100%;
        }
      }
    }

    .dream-job {
      padding-top: var(--spacing-80);

      .container {
        .h2 {
          font-size: 32px;
        }

        .content {
          .row {
            .text {
              padding-bottom: var(--spacing-8);
            }

            .track-dot {
              left: 9px;
            }
          }
        }

        .track-certificate {
          margin-top: var(--spacing-32);
        }
      }
    }

    ::v-deep .pre-footer {
      padding-top: var(--spacing-80);

      .container {
        .row {
          margin: 0;

          .col {
            padding: 0;

            .subtitle {
              line-height: 150%;
            }
          }

          .row-btn {
            a {
              height: 58px;
              width: 100%;
              margin: 0;
            }
          }
        }
      }

      .bg {
        margin-top: var(--spacing-60);
      }
    }
  }
}

.tracking-section.physical-certificate-section {
  .hero-section-wrapper.physical-hero.site {
    .wall {
      img {
        object-fit: cover;
        object-position: 70%;
      }
    }
  }
}

.fa {
  @import '../../assets/sass/fa/pages/career-track.scss';
}
</style>
